<template>
  <div
    class="dui-editor"
    :class="{ 'indent': indent }"
  >
    <DuiEditorToolbar
      ref="toolbar"
      :display-word-count="displayWordCount"
      :options="vm.toolbarOptions"
      :toolbar-fixed-top="toolbarFixedTop"
      :toolbar-width="vm.toolbarWidth"
      :word-count="vm.wordCount"
    />

    <div
      v-if="showChapterTitle"
      class="chapter-title"
    >
      <div class="chapter-number">
        {{ chapterNumber }}
      </div>

      <input
        v-model="vm.chapterTitle"
        placeholder="Chapter Title"
        type="text"
        @input="updateContent"
      >
    </div>

    <div ref="textEditor" />
  </div>
</template>

<script>
import 'quill/dist/quill.core.css';
import 'quill/dist/quill.snow.css';
import Quill from '@dui/quill/quill';

import DuiEditorToolbar from './dui-editor-toolbar.vue';
import duiWordCount from '@dui/filters/dui-word-count/dui-word-count';
import QuillHelper from '@dui/quill/helpers/quill-helper';

import storeKey from '@dui/mixins/store-key';

const DEFAULT_OPTIONS = {
  modules: {
    toolbar: {
      container: null,
    },
  },
  theme: 'discovery',
  formats: [
    'bold',
    'italic',
    'underline',
    'blockquote',
  ],
};

const EDITOR_OFFSET = 74;
const EMPTY_EDITOR_CONTENT = {
  html: '<p class="ends-with-break"><br></p>',
  text: '\n',
};

function getToolbarOptions(options) {
  const toolbarOptions = [];

  options.forEach((option) => {
    const toolbarOption = {
      class: `ql-${ option }`,
      format: option,
      name: option,
    };

    toolbarOptions.push(toolbarOption);
  });

  return toolbarOptions;
}

export default {
  components: {
    DuiEditorToolbar,
  },
  mixins: [
    storeKey,
  ],
  props: {
    chapterNumber: {
      type: Number,
      default: 1,
    },
    displayWordCount: {
      type: Boolean,
      default: true,
    },
    fixToolbarTop: {
      type: Boolean,
      default: false,
    },
    indent: {
      type: Boolean,
      default: true,
    },
    placeholder: {
      type: String,
      default: 'Start writing here...',
    },
    showChapterTitle: {
      type: Boolean,
      default: false,
    },
    toolbarFixedTop: {
      type: Number,
      default: 0,
    },
    value: {
      type: Object,
      default: null,
    },
  },
  data: () => ({
    vm: {
      chapterTitle: null,
      editor: null,
      editorContent: {
        chapterTitle: null,
        html: null,
        text: null,
      },
      toolbarOptions: [],
      toolbarWidth: null,
      wordCount: 0,
    },
  }),
  computed: {
    storeContent() {
      return this.loadFromStore();
    },
  },
  watch: {
    storeContent() {
      this.setLoadedContent(this.storeContent);
    },
    value() {
      this.setLoadedContent(this.value);
    },
  },
  beforeDestroy() {
    this.removeEventListeners();
  },
  async mounted() {
    await this.initQuill();
    this.setEditorContent();
    this.addEventListeners();
  },
  methods: {
    adjustFixedToolbar() {
      const editorHeight = this.$el.clientHeight;
      const editorTopPosition = this.$el.getBoundingClientRect().top;

      const shouldFix = (editorTopPosition <= this.toolbarFixedTop);
      this.$el.classList.toggle('fixed-toolbar', shouldFix);

      if (shouldFix) {
        this.vm.toolbarWidth = this.$el.clientWidth;

        const isOffBounds = (editorTopPosition < -(editorHeight - EDITOR_OFFSET));
        this.$el.classList.toggle('off-bounds', isOffBounds);
        return;
      }

      this.vm.toolbarWidth = null;
      this.$el.classList.remove('off-bounds');
    },

    clearContent() {
      this.vm.editor.setContents([
        {
          insert: '\n',
        },
      ]);
    },

    async initQuill() {
      let options = Object.assign({}, DEFAULT_OPTIONS);
      // Set behaviour configuration
      options = QuillHelper.addDefaultBehaviour(options);

      options.placeholder = this.placeholder;
      this.vm.toolbarOptions = getToolbarOptions(options.formats);

      await this.$nextTick(); // Needed for Quill to set up the toolbar
      const toolbarEl = this.$el.querySelector('.dui-editor-toolbar');
      options.modules.toolbar.container = toolbarEl;

      this.vm.editor = new Quill(this.$refs.textEditor, options);
    },

    setEditorContent() {
      if (this.value) {
        this.setLoadedContent(this.value);
        return;
      }

      if (!this.storeLoadKey) {
        return;
      }

      this.setLoadedContent(this.storeContent);
    },

    setLoadedContent(content) {
      if (content.chapterTitle && (content.chapterTitle !== this.vm.chapterTitle)) {
        this.vm.chapterTitle = content.chapterTitle;
      }

      if (content.html && (content.html !== this.vm.editor.root.innerHTML)) {
        this.vm.editor.root.innerHTML = content.html;
        return;
      }

      if (content.text && (content.text !== this.vm.editor.getText())) {
        this.vm.editor.setText(content.text, Quill.sources.USER);
      }
    },

    updateContent() {
      const htmlContent = (this.vm.editor.root.innerHTML !== EMPTY_EDITOR_CONTENT.html) ?
        this.vm.editor.root.innerHTML :
        '';

      const textContent = (this.vm.editor.getText() !== EMPTY_EDITOR_CONTENT.text) ?
        this.vm.editor.getText() :
        '';

      this.vm.editorContent.html = htmlContent;
      this.vm.editorContent.text = textContent;

      this.$emit('html-updated', this.vm.editorContent.html);
      this.$emit('text-updated', this.vm.editorContent.text);

      if (this.showChapterTitle) {
        this.vm.editorContent.chapterTitle = this.vm.chapterTitle;
        this.$emit('chapter-title-updated', this.vm.editorContent.chapterTitle);
      }

      this.$emit('input', this.vm.editorContent);
      this.$emit('change');

      if (this.storeUpdateKey) {
        this.$store.dispatch(this.storeUpdateKey, this.vm.editorContent);
      }
    },

    updateWordCount() {
      if (!this.displayWordCount) {
        return;
      }

      this.$set(this.vm, 'wordCount', duiWordCount(this.vm.editor.getText()));
    },

    // Events
    addEventListeners() {
      this._boundFns = {
        textChange: this.textChangePerformed.bind(this),
        windowChange: this.windowChangePerformed.bind(this),
      };

      this.vm.editor.on('text-change', this._boundFns.textChange);

      if (this.fixToolbarTop) {
        window.addEventListener('resize', this._boundFns.windowChange);
        window.addEventListener('scroll', this._boundFns.windowChange);
      }
    },

    removeEventListeners() {
      this.vm.editor.off('text-change', this._boundFns.textChange);

      window.removeEventListener('resize', this._boundFns.windowChange);
      window.removeEventListener('scroll', this._boundFns.windowChange);
    },

    textChangePerformed() {
      this.updateContent();
      this.updateWordCount();
    },

    windowChangePerformed() {
      this.adjustFixedToolbar();
    },
    // END Events
  },
};
</script>

<style lang="scss">
@import "@dui-styles/base";

.dui-editor {
  background-color: getColor($white);

  &[disabled] {
    opacity: $disabled-opacity;
    pointer-events: none;
  }

  &.fixed-toolbar {
    padding-top: 52px;

    &.off-bounds .dui-editor-toolbar {
      opacity: 0;
    }

    .dui-editor-toolbar {
      opacity: 1;
      position: fixed;
      top: 0;
      z-index: 1;

      @include transition((
        opacity
      ), $transition-time-fast);
    }
  }

  &.indent {
    .ql-container.ql-snow .ql-editor {
      p {
        text-indent: $_4em;

        &:first-child {
          text-indent: 0;
        }
      }

      blockquote.ends-with-break + blockquote,
      p.ends-with-break + p {
        text-indent: 0;
      }
    }
  }

  .chapter-title {
    padding: $_6em 0 $_4em;
    text-align: center;

    .chapter-number {
      font-size: $font-size-xxxl;
    }

    input {
      border: none;
      font-family: $alt-font;
      font-size: $font-size-xxl;
      margin-top: $_4em;
      text-align: center;
      width: 100%;
    }
  }

  .ql-container.ql-snow {
    border: none;

    .ql-editor {
      @include editorFontStyle();
      padding: $_2em 0;

      &:before {
        color: getColor($grey, dark);
        font-style: normal;
        left: 0;
        right: initial;
      }

      blockquote {
        margin: 0;
      }

      strong {
        font-weight: $base-weight-heavy;
      }
    }
  }
}
</style>
